import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EngineeringIcon from "@mui/icons-material/Engineering";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { HideIfNoRole } from "../../../auth/authUi";
import AxionControls from "../../../components/controls/AxionControls";
import { activateTrade, deactivateTrade, getTrades } from "../../../fetchers/cmmsTrade";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import { CMMS_MANAGER_ROLES } from "../../cmms/cmmsRoles";
import cmmsMessages from "../../cmms/cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, formatCmmsError } from "../../cmms/components/CmmsStates";
import {
  getAssetHierarchyPalette,
  getContainedButtonSx,
  getSwitchSx,
} from "../../cmms/assetHierarchyStyles";
import messages from "../messages";
import TradesDataGrid from "./TradesDataGrid";
import TradeFormDialog from "./TradeFormDialog";
import TradeMembersDialog from "./TradeMembersDialog";
import TradeDepartmentsDialog from "./TradeDepartmentsDialog";

/*
 * The trades (yrkesgrupper) tab of the CMMS settings page.
 *
 * SparePartTypesPanel with a different noun, on purpose: three registers on one settings page
 * that filter, retire and restore differently would be the inconsistency, not the copy.
 *
 * The query key is cmmsTrades and nothing else reads it yet. The work order side will, and when it
 * does a trade added here reaches it without a reload, the same way a new spare part type reaches
 * the spare part form.
 */
export default function TradesPanel() {
  const theme = useTheme();
  const intl = useIntl();
  const queryClient = useQueryClient();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [formState, setFormState] = useState({ open: false, trade: null });
  const [membersState, setMembersState] = useState({ open: false, trade: null });
  const [departmentsState, setDepartmentsState] = useState({ open: false, trade: null });
  const [confirmation, setConfirmation] = useState(null);

  const tradesQuery = useQuery(
    ["cmmsTrades", companyKey, includeInactive],
    () => getTrades(companyKey, includeInactive),
    { enabled: Boolean(companyKey), staleTime: 30000 },
  );

  const trades = useMemo(() => tradesQuery.data ?? [], [tradesQuery.data]);

  // The endpoint owns which rows exist — that is what the switch below asks it — and the browser
  // owns which of them are on screen. The list arrives whole, so the text is here to be matched.
  //
  // Departments are in the needle for the reason the notifications tab gives: "which trade gets
  // Formsprutning's work orders" is a question this tab answers, and the column alone leaves it
  // to be found by eye.
  const visibleTrades = useMemo(() => {
    const needle = searchText.trim().toLowerCase();
    if (!needle) return trades;
    return trades.filter((trade) => {
      const departments = (trade.departments ?? []).map((department) => department.name).join(" ");
      return `${trade.name ?? ""} ${departments}`.toLowerCase().includes(needle);
    });
  }, [trades, searchText]);

  const deactivateMutation = useMutation({
    mutationFn: (id) => deactivateTrade(companyKey, id),
  });

  const restoreMutation = useMutation({
    mutationFn: (id) => activateTrade(companyKey, id),
  });

  const hasActiveFilters = includeInactive || searchText.trim() !== "";

  const handleClearFilters = () => {
    setIncludeInactive(false);
    setSearchText("");
  };

  const handleAdd = () => setFormState({ open: true, trade: null });
  const handleEdit = (row) => setFormState({ open: true, trade: row });
  const closeForm = () => setFormState({ open: false, trade: null });

  const handleMembers = (row) => setMembersState({ open: true, trade: row });
  const closeMembers = () => setMembersState({ open: false, trade: null });

  const handleDepartments = (row) => setDepartmentsState({ open: true, trade: row });
  const closeDepartments = () => setDepartmentsState({ open: false, trade: null });

  const handleDeactivate = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.tradeDeactivateTitle),
      message: intl.formatMessage(messages.tradeDeactivateMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.deactivateConfirm),
      onConfirm: async () => {
        try {
          await deactivateMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsTrades"] });
          pushNotification(intl.formatMessage(messages.tradeToastDeactivated));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tradeDeactivateFailed)));
        }
      },
    });
  };

  const handleRestore = (row) => {
    setConfirmation({
      title: intl.formatMessage(messages.tradeRestoreTitle),
      message: intl.formatMessage(messages.tradeRestoreMessage, { name: row.name }),
      confirmText: intl.formatMessage(messages.restoreConfirm),
      onConfirm: async () => {
        try {
          await restoreMutation.mutateAsync(row.id);
          setConfirmation(null);
          await queryClient.invalidateQueries({ queryKey: ["cmmsTrades"] });
          pushNotification(intl.formatMessage(messages.tradeToastRestored));
        } catch (error) {
          pushErrorMessage(formatCmmsError(error, intl.formatMessage(messages.tradeRestoreFailed)));
        }
      },
    });
  };

  const isMutating = deactivateMutation.isPending || deactivateMutation.isLoading
    || restoreMutation.isPending || restoreMutation.isLoading;

  return (
    <Box>
      {/* No heading of its own — the tab above already says Trades. */}
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        <FormattedMessage {...messages.tradesSubtitle} />
      </Typography>

      <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" sx={{ gap: 1, mb: 2 }}>
        {/*
          * Behind the route's own Admin gate as well, as on the other tabs: a button that 403s is
          * worse than one that was never offered. The module's own button styling, because a bare
          * contained button is the colour of the surface behind it.
          */}
        <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAdd}
            sx={getContainedButtonSx(cmmsColors)}
          >
            <FormattedMessage {...messages.addTrade} />
          </Button>
        </HideIfNoRole>

        <TextField
          size="small"
          sx={{ width: { xs: "100%", sm: 280 } }}
          value={searchText}
          onChange={(event) => setSearchText(event.target.value)}
          label={intl.formatMessage(messages.filterSearch)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        {/*
          * Server-side, and the only way to a retired row: the endpoint leaves them out by
          * default, so without this switch there is no way to reach the restore button.
          */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={includeInactive}
              onChange={(event) => setIncludeInactive(event.target.checked)}
              sx={getSwitchSx(cmmsColors)}
            />
          }
          label={intl.formatMessage(messages.filterShowInactive)}
        />
      </Stack>

      {tradesQuery.error &&
        <CmmsErrorState
          error={tradesQuery.error}
          fallback={intl.formatMessage(messages.tradesLoadFailed)}
          onRetry={() => tradesQuery.refetch()}
          isRetrying={tradesQuery.isFetching}
        />
      }
      {!tradesQuery.error &&
        <TradesDataGrid
          trades={visibleTrades}
          // First load only, so a background refetch does not replace the list with a skeleton.
          isLoading={tradesQuery.isLoading}
          showStatusColumn={includeInactive}
          onEdit={handleEdit}
          onMembers={handleMembers}
          onDepartments={handleDepartments}
          onDeactivate={handleDeactivate}
          onRestore={handleRestore}
          emptyState={
            hasActiveFilters
              ? <CmmsEmptyState
                  icon={<FilterAltOffIcon />}
                  title={intl.formatMessage(messages.tradesEmptyFilteredTitle)}
                  hint={intl.formatMessage(messages.emptyFilteredHint)}
                  action={
                    <Button size="small" variant="outlined" color="secondary" onClick={handleClearFilters}>
                      <FormattedMessage {...messages.filterClear} />
                    </Button>
                  }
                />
              : <CmmsEmptyState
                  icon={<EngineeringIcon />}
                  title={intl.formatMessage(messages.tradesEmptyTitle)}
                  hint={intl.formatMessage(messages.tradesEmptyHint)}
                  action={
                    <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={handleAdd}
                        sx={getContainedButtonSx(cmmsColors)}
                      >
                        <FormattedMessage {...messages.addTrade} />
                      </Button>
                    </HideIfNoRole>
                  }
                />
          }
        />
      }

      <TradeFormDialog
        open={formState.open}
        trade={formState.trade}
        companyKey={companyKey}
        onClose={closeForm}
      />

      <TradeMembersDialog
        open={membersState.open}
        trade={membersState.trade}
        companyKey={companyKey}
        onClose={closeMembers}
      />

      <TradeDepartmentsDialog
        open={departmentsState.open}
        trade={departmentsState.trade}
        companyKey={companyKey}
        onClose={closeDepartments}
      />

      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
